import { Button, Modal } from '@clearcover/atlas';
import {
  CoverageOptionsResponse,
  DisplayCoverageType,
  VehicleType,
} from '@clearcover/core-app-types';
import React, { FC, useContext, useEffect, useState } from 'react';
import styles from './styles.module.css';

import { Card, CardHeader, ExpansionPanel } from 'Shared/components';

import {
  editPolicyCoveragePermissionsPolicy,
  editVehicleCoveragePermissionsPolicy,
} from 'Admin/authPolicies';
import { PolicyContext } from 'Policies/utils';
import { useModalHashChange } from 'Shared/hooks';
import { combineStrings } from '../../../Shared/utils';
import { ConflictingDraftModal } from '../ConflictingDraftModal';
import { EditCoveragesForm, EditCoveragesFormProps } from './EditCoveragesForm';
import { useApi } from 'Shared/services/Api';

type CoveragesType = Array<{
  label: string;
  value: string;
  coverageIdentifier: string;
  displayValue: string;
  parentCoverage?: string;
}>;
type PolicyCoveragesSectionType = {
  vehicles: Array<VehicleType>;
  policyCoverages: CoveragesType;
  policyEndorsements: CoveragesType;
};

const mapToSelectedCoverages = (coverages: Array<DisplayCoverageType>) =>
  coverages.reduce<Record<string, string>>(
    (acc, c) => ({
      ...acc,
      [c.coverageIdentifier]: c.value,
    }),
    {}
  );

export const PolicyCoveragesSection: FC<PolicyCoveragesSectionType> = ({
  vehicles,
  policyCoverages,
  policyEndorsements,
}) => {
  const {
    canEndorseDuringRegionalEvents,
    isEndorseablePolicy,
    resourceEffectiveGrants,
    details,
  } = useContext(PolicyContext);
  const [hasParentCoverage, setHasParentCoverage] = useState(false);

  const [endorsementCoverages, setEndorsementCoverages] =
    useState<CoveragesType>(policyEndorsements);

  const { modalHashChange } = useModalHashChange('editCoverages');

  const [{ data: coverageOptionsResponse, loading: loadingCoverageOptions }] =
    useApi<CoverageOptionsResponse>(
      `/options/coverages?identifier=${details.filingIdentifier}`
    );

  useEffect(() => {
    // updating policy endorsements with parentCoverage and displayValue from the coverage options response,
    // if policy endorsements are not found in the coverage options response, we will use the waived endorsement
    if (coverageOptionsResponse) {
      const checkForParentCoverage =
        coverageOptionsResponse.policy.endorsements.some(
          (endorsement) => 'parentCoverage' in endorsement
        ) ||
        coverageOptionsResponse.policy.coverages.some(
          (coverage) => 'parentCoverage' in coverage
        );

      setHasParentCoverage(checkForParentCoverage);

      if (checkForParentCoverage) {
        const updatedPolicyEndorsements: CoveragesType =
          coverageOptionsResponse.policy.endorsements.map((endorsement) => {
            const endorsementFromPolicy = policyEndorsements.find(
              (e) => e.coverageIdentifier === endorsement.identifier
            );

            // if endorsementFromPolicy is not found, we need to find the option value for the waived endorsement
            const optionValue = endorsementFromPolicy
              ? endorsement.parentCoverageOptionsSynonyms?.find(
                  (o: any) => o.value === endorsementFromPolicy?.value
                )
              : endorsement.parentCoverageOptionsSynonyms?.find(
                  (o: any) => o.value === 'waived'
                );

            return {
              label: endorsementFromPolicy?.label || endorsement.label,
              displayValue:
                optionValue?.label || endorsementFromPolicy?.displayValue || '',
              coverageIdentifier:
                endorsementFromPolicy?.coverageIdentifier ||
                endorsement.identifier,
              value: endorsementFromPolicy?.value || optionValue?.value || '',
              parentCoverage: endorsement.parentCoverage || undefined,
            };
          });

        setEndorsementCoverages(updatedPolicyEndorsements);
      }
    }
  }, [coverageOptionsResponse, policyEndorsements]);

  const canEditCoverages =
    canEndorseDuringRegionalEvents &&
    isEndorseablePolicy &&
    (editPolicyCoveragePermissionsPolicy(resourceEffectiveGrants) ||
      editVehicleCoveragePermissionsPolicy(resourceEffectiveGrants));

  const coveragesToDisplay = hasParentCoverage
    ? [...policyCoverages, ...endorsementCoverages]
        .filter((coverage) => !coverage.parentCoverage)
        .map((coverage) => {
          const stackedCoverage = endorsementCoverages.filter(
            (endorsement) =>
              endorsement.parentCoverage === coverage.coverageIdentifier
          );
          if (stackedCoverage.length > 0) {
            return {
              ...coverage,
              displayValue: `${coverage.displayValue} | ${stackedCoverage
                .map((s) => s.displayValue)
                .join(' | ')}`,
            };
          }
          return coverage;
        })
    : [...policyCoverages, ...endorsementCoverages];

  if (loadingCoverageOptions) {
    return <div>Loading...</div>;
  }

  return (
    <Card id={'coverages-card'}>
      <div className={styles.cardHeader}>
        <CardHeader title={'Coverages'} />
        {canEditCoverages && (
          <Modal
            onVisibilityChange={modalHashChange}
            variant={'full'}
            toggle={(openEditCoveragesModal) => (
              <ConflictingDraftModal
                onApprove={openEditCoveragesModal}
                renderToggle={(openModal) => (
                  <Button
                    className={styles.editCoveragesButton}
                    size="sm"
                    variant="secondary"
                    onClick={openModal}
                    data-testid="open-edit-coverages-modal"
                  >
                    Edit
                  </Button>
                )}
              />
            )}
          >
            {(close) => (
              <EditCoveragesForm
                close={close}
                vehicles={vehicles.reduce<EditCoveragesFormProps['vehicles']>(
                  (acc, v) => ({
                    ...acc,
                    [v.identifier]: {
                      display: combineStrings([v.year, v.make, v.model]),
                      selectedCoverages: mapToSelectedCoverages(v.coverages),
                      selectedEndorsements: mapToSelectedCoverages(
                        v.endorsements
                      ),
                    },
                  }),
                  {}
                )}
                selectedPolicyCoverages={mapToSelectedCoverages(
                  policyCoverages
                )}
                selectedPolicyEndorsements={mapToSelectedCoverages(
                  policyEndorsements
                )}
              />
            )}
          </Modal>
        )}
      </div>
      <ExpansionPanel
        head={
          <div className={styles.title}>
            <span>Policy Level</span>
          </div>
        }
      >
        <div className={styles.table} data-testid="coverages">
          {coveragesToDisplay.map((coverage, index) => (
            <div
              key={coverage.label}
              className={styles.coverage}
              data-testid={`coverage-${index}`}
            >
              <div>{coverage.label}</div>
              <div className={styles.coverageValue}>
                {coverage.displayValue}
              </div>
            </div>
          ))}
        </div>
      </ExpansionPanel>
      {vehicles.map((vehicle, index) => {
        return (
          <ExpansionPanel
            id={`vehicle-${index}-coverage`}
            key={`${vehicle.vin}-${index}`}
            head={
              <div className={styles.title}>
                <span>
                  {combineStrings([vehicle.year, vehicle.make, vehicle.model])}
                </span>
              </div>
            }
          >
            <div className={styles.table}>
              {[...vehicle.coverages, ...vehicle.endorsements].map(
                (c, index) => (
                  <div
                    key={c.label}
                    className={styles.coverage}
                    data-testid={`coverage-${index}`}
                  >
                    <div>{c.label}</div>
                    <div className={styles.coverageValue}>{c.displayValue}</div>
                  </div>
                )
              )}
            </div>
          </ExpansionPanel>
        );
      })}
    </Card>
  );
};
